// The job sheet's pieces (pages/ux-full components/jobs.js: PhotoStrip): a job's photos in order, each
// saying whether the customer sees it. It draws no frame: the sheet puts it in a section. Opening one
// full size and changing who sees it are the screen's callbacks, drawn only when the screen passes them.
import { useState } from 'react';
import type { PhotoStripProps } from '../job-schemas.ts';
import { Label } from '../primitives.tsx';

type Photo = PhotoStripProps['photos'][number];

/** Its words for a screen reader and the "Open … full size" button: its note, else its kind. */
const what = (p: Photo) => p.caption ?? `${p.tag} ${p.tile.t === 'video' ? 'video' : 'photo'}`;

function Thumb({ p, onOpen }: { p: Photo; onOpen?: ((id: string) => void) | undefined }) {
  const t = p.tile;
  if ((t.t === 'picture' || t.t === 'video') && onOpen)
    return (
      <button
        type="button"
        className="flw-thumb"
        aria-label={`Open ${what(p)} full size`}
        onClick={() => onOpen(p.id)}
      >
        {t.t === 'picture' ? <img src={t.src} alt="" loading="lazy" /> : <span>▶ Video</span>}
      </button>
    );
  if (t.t === 'picture')
    return (
      <span className="flw-thumb">
        <img src={t.src} alt={what(p)} loading="lazy" />
      </span>
    );
  if (t.t === 'file')
    return (
      <a className="flw-thumb" href={t.href ?? undefined} target="_blank" rel="noreferrer">
        <span>{t.words}</span>
      </a>
    );
  return (
    <span className="flw-thumb">
      <span>{t.t === 'video' ? '▶ Video' : t.words}</span>
    </span>
  );
}

/**
 * A job's photos, videos and documents (attachments.list): how many the customer sees, then each with
 * its kind, its note, who took it and when, and whether the customer sees it. The first few show, then
 * "Show all", so a job with hundreds never draws them all at once. Its other files follow as links.
 */
export function PhotoStrip({
  data,
  onOpen,
  onShare,
  busy = false,
}: {
  data: PhotoStripProps;
  /** Full size (the screen's viewer), from this one. */
  onOpen?: (id: string) => void;
  /** Show a photo to the customer, or keep it in the office (attachments.setVisibility): the screen's. */
  onShare?: (id: string, shared: boolean) => void;
  /** A change is on its way: nothing can be pressed twice. */
  busy?: boolean;
}) {
  const [all, setAll] = useState(false);
  const n = data.photos.length;
  if (!n && !data.files.length)
    return (
      <p className="flw-empty" role="status">
        No photos on this job yet. The technicians take them on the iPad.
      </p>
    );
  const shown = all ? data.photos : data.photos.slice(0, data.first);
  const seen = data.photos.filter((p) => p.shared).length;
  const waiting = data.photos.filter((p) => p.tile.t === 'waiting').length;
  return (
    <>
      {n > 0 && (
        <p className="flw-strip-lede">
          {seen} of {n} the customer sees
          {waiting > 0 && (
            <>
              ; <Label kind="wait">{waiting} not ready to show yet</Label>
            </>
          )}
          .
        </p>
      )}
      {n > 0 && (
        <ul className="flw-strip" aria-label={`Photos on job #${data.jobNumber}`}>
          {shown.map((p) => (
            <li key={p.id} className="flw-ph" data-s={p.tile.t}>
              <Thumb p={p} onOpen={onOpen} />
              <b className="flw-ph-cap">
                {/* its kind and its note, else its made-up name ("Before 2"): never its file's name */}
                <span className="flw-ph-tag">{p.caption ? `${p.tag}:` : (p.name ?? p.tag)}</span>
                {p.caption && ` ${p.caption}`}
              </b>
              {p.by && <span className="flw-sub">{p.by}</span>}
              {/* the one action on a photo: who sees it, said as a word, and the change */}
              {p.canShare && (
                <span className="flw-ph-vis">
                  <Label kind={p.shared ? 'now' : 'muted'}>
                    {p.shared ? 'Customer sees it' : 'Office only'}
                  </Label>
                  {onShare && (
                    <button
                      type="button"
                      className="flw-link"
                      disabled={busy}
                      aria-label={`${p.shared ? 'Hide from the customer' : 'Show to the customer'}: ${what(p)}`}
                      onClick={() => onShare(p.id, !p.shared)}
                    >
                      {p.shared ? 'Hide' : 'Show'}
                    </button>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {!all && n > data.first && (
        <div className="flw-acts">
          <button type="button" className="flw-link" onClick={() => setAll(true)}>
            Show all {n}
          </button>
        </div>
      )}
      {data.files.length > 0 && (
        <ul className="flw-rows flw-files" aria-label="Other files">
          {data.files.map((f) => (
            <li key={f.id} className="flw-row">
              {f.href ? (
                <a href={f.href} target="_blank" rel="noreferrer" title={f.file ?? undefined}>
                  {f.name}
                </a>
              ) : (
                <span>{f.name}</span>
              )}
              <span className="flw-sub">{f.type}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
